import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { claimAudioPlayback, type AudioPlaybackClaim } from "../utils/audioPlaybackCoordinator";

interface UseSpeechSynthesisOptions {
  defaultEnabled?: boolean;
  gatewayUrl?: string | null;
  lang?: string;
  preferGateway?: boolean;
}

export type SpeechPlaybackFinishReason = "ended" | "error" | "interrupted" | "skipped";

export interface SpeechPlaybackResult {
  finished: Promise<SpeechPlaybackFinishReason>;
  runId: number;
}

const defaultVoiceGatewayUrl = "ws://127.0.0.1:8787/voice";
const defaultTtsGatewayUrl = "http://127.0.0.1:8787/tts";
const sameOriginGatewayValue = "__SAME_ORIGIN__";

export function useSpeechSynthesis({
  defaultEnabled = true,
  gatewayUrl,
  lang = "zh-CN",
  preferGateway = true
}: UseSpeechSynthesisOptions = {}) {
  const abortControllerRef = useRef<AbortController | null>(null);
  const audioObjectUrlRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const finishResolversRef = useRef(new Map<number, (reason: SpeechPlaybackFinishReason) => void>());
  const playbackClaimRef = useRef<AudioPlaybackClaim | null>(null);
  const runIdRef = useRef(0);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const resolvedGatewayUrl = useMemo(() => resolveTtsGatewayUrl(gatewayUrl), [gatewayUrl]);
  const [enabled, setEnabled] = useState(defaultEnabled);
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(false);

  const finishSpeech = useCallback((runId: number, reason: SpeechPlaybackFinishReason) => {
    const resolve = finishResolversRef.current.get(runId);

    if (!resolve) {
      return;
    }

    finishResolversRef.current.delete(runId);
    resolve(reason);
  }, []);

  const createPlaybackResult = useCallback((runId: number): SpeechPlaybackResult => {
    let resolveFinished: (reason: SpeechPlaybackFinishReason) => void = () => undefined;
    const finished = new Promise<SpeechPlaybackFinishReason>((resolve) => {
      resolveFinished = resolve;
    });

    finishResolversRef.current.set(runId, resolveFinished);

    return { finished, runId };
  }, []);

  const cleanupRemotePlayback = useCallback(() => {
    playbackClaimRef.current?.release();
    playbackClaimRef.current = null;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
      audioRef.current.load();
      audioRef.current = null;
    }

    if (audioObjectUrlRef.current && typeof URL !== "undefined") {
      URL.revokeObjectURL(audioObjectUrlRef.current);
      audioObjectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    setSupported(canUseGatewayTts(preferGateway, resolvedGatewayUrl) || canUseBrowserTts());

    return () => {
      cleanupRemotePlayback();
      window.speechSynthesis?.cancel();
    };
  }, [cleanupRemotePlayback, preferGateway, resolvedGatewayUrl]);

  const stop = useCallback(() => {
    const currentRunId = runIdRef.current;
    runIdRef.current += 1;
    finishSpeech(currentRunId, "interrupted");
    cleanupRemotePlayback();
    window.speechSynthesis?.cancel();
    utteranceRef.current = null;
    setSpeaking(false);
  }, [cleanupRemotePlayback, finishSpeech]);

  useEffect(() => {
    if (!enabled) {
      stop();
    }
  }, [enabled, stop]);

  const speakWithBrowser = useCallback((content: string, runId: number) => {
    if (!canUseBrowserTts()) {
      return false;
    }

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(content);
    utterance.lang = lang;
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;
    utterance.voice = pickChineseVoice(window.speechSynthesis.getVoices(), lang);
    utterance.onend = () => {
      if (runIdRef.current === runId) {
        setSpeaking(false);
      }
      playbackClaimRef.current?.release();
      playbackClaimRef.current = null;
      finishSpeech(runId, "ended");
    };
    utterance.onerror = () => {
      if (runIdRef.current === runId) {
        setSpeaking(false);
      }
      playbackClaimRef.current?.release();
      playbackClaimRef.current = null;
      finishSpeech(runId, "error");
    };
    utteranceRef.current = utterance;
    setSpeaking(true);
    window.speechSynthesis.speak(utterance);
    return true;
  }, [finishSpeech, lang]);

  const speakWithGateway = useCallback(async (content: string, runId: number) => {
    if (!canUseGatewayTts(preferGateway, resolvedGatewayUrl)) {
      return false;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(resolvedGatewayUrl, {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({ text: content }),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`TTS gateway failed: ${response.status}`);
      }

      const audioBlob = await response.blob();

      if (controller.signal.aborted || runIdRef.current !== runId) {
        finishSpeech(runId, "interrupted");
        return true;
      }

      const objectUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(objectUrl);
      audioObjectUrlRef.current = objectUrl;
      audioRef.current = audio;
      audio.onended = () => {
        if (runIdRef.current === runId) {
          setSpeaking(false);
        }
        playbackClaimRef.current?.release();
        playbackClaimRef.current = null;
        finishSpeech(runId, "ended");
        cleanupRemotePlayback();
      };
      audio.onerror = () => {
        playbackClaimRef.current?.release();
        playbackClaimRef.current = null;
        cleanupRemotePlayback();
        if (runIdRef.current === runId && speakWithBrowser(content, runId)) {
          return;
        }

        if (runIdRef.current === runId) {
          setSpeaking(false);
        }
        finishSpeech(runId, "error");
      };

      await audio.play();
      return true;
    } catch {
      if (controller.signal.aborted || runIdRef.current !== runId) {
        return true;
      }

      cleanupRemotePlayback();
      return false;
    }
  }, [cleanupRemotePlayback, finishSpeech, preferGateway, resolvedGatewayUrl, speakWithBrowser]);

  const speak = useCallback((text: string) => {
    const content = text.trim();

    if (!enabled || !content) {
      return {
        finished: Promise.resolve<SpeechPlaybackFinishReason>("skipped"),
        runId: runIdRef.current
      };
    }

    finishSpeech(runIdRef.current, "interrupted");
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    const playbackResult = createPlaybackResult(runId);
    cleanupRemotePlayback();
    window.speechSynthesis?.cancel();
    utteranceRef.current = null;
    playbackClaimRef.current = claimAudioPlayback(() => {
      runIdRef.current += 1;
      finishSpeech(runId, "interrupted");
      cleanupRemotePlayback();
      window.speechSynthesis?.cancel();
      utteranceRef.current = null;
      setSpeaking(false);
    });
    setSpeaking(true);

    if (canUseGatewayTts(preferGateway, resolvedGatewayUrl)) {
      void speakWithGateway(content, runId).then((startedGatewayAudio) => {
        if (startedGatewayAudio || runIdRef.current !== runId) {
          return;
        }

        if (!speakWithBrowser(content, runId)) {
          setSpeaking(false);
          finishSpeech(runId, "error");
        }
      });
      return playbackResult;
    }

    if (!speakWithBrowser(content, runId)) {
      setSpeaking(false);
      finishSpeech(runId, "error");
    }

    return playbackResult;
  }, [
    cleanupRemotePlayback,
    createPlaybackResult,
    enabled,
    finishSpeech,
    preferGateway,
    resolvedGatewayUrl,
    speakWithBrowser,
    speakWithGateway
  ]);

  const speakAudio = useCallback((audioSrc: string, fallbackText = "") => {
    const resolvedAudioSrc = audioSrc.trim();
    const fallbackContent = fallbackText.trim();

    if (!enabled || !resolvedAudioSrc) {
      return fallbackContent
        ? speak(fallbackContent)
        : {
            finished: Promise.resolve<SpeechPlaybackFinishReason>("skipped"),
            runId: runIdRef.current
          };
    }

    finishSpeech(runIdRef.current, "interrupted");
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    const playbackResult = createPlaybackResult(runId);
    cleanupRemotePlayback();
    window.speechSynthesis?.cancel();
    utteranceRef.current = null;
    playbackClaimRef.current = claimAudioPlayback(() => {
      runIdRef.current += 1;
      finishSpeech(runId, "interrupted");
      cleanupRemotePlayback();
      window.speechSynthesis?.cancel();
      utteranceRef.current = null;
      setSpeaking(false);
    });
    setSpeaking(true);

    const audio = new Audio(resolvedAudioSrc);
    audio.preload = "auto";
    audioRef.current = audio;
    audio.onended = () => {
      if (runIdRef.current === runId) {
        setSpeaking(false);
      }
      playbackClaimRef.current?.release();
      playbackClaimRef.current = null;
      finishSpeech(runId, "ended");
      cleanupRemotePlayback();
    };
    audio.onerror = () => {
      playbackClaimRef.current?.release();
      playbackClaimRef.current = null;
      cleanupRemotePlayback();

      if (runIdRef.current !== runId) {
        finishSpeech(runId, "interrupted");
        return;
      }

      if (!fallbackContent) {
        setSpeaking(false);
        finishSpeech(runId, "error");
        return;
      }

      if (canUseGatewayTts(preferGateway, resolvedGatewayUrl)) {
        void speakWithGateway(fallbackContent, runId).then((startedGatewayAudio) => {
          if (startedGatewayAudio || runIdRef.current !== runId) {
            return;
          }

          if (!speakWithBrowser(fallbackContent, runId)) {
            setSpeaking(false);
            finishSpeech(runId, "error");
          }
        });
        return;
      }

      if (!speakWithBrowser(fallbackContent, runId)) {
        setSpeaking(false);
        finishSpeech(runId, "error");
      }
    };

    void audio.play().catch(() => {
      audio.onerror?.(new Event("error"));
    });

    return playbackResult;
  }, [
    cleanupRemotePlayback,
    createPlaybackResult,
    enabled,
    finishSpeech,
    preferGateway,
    resolvedGatewayUrl,
    speak,
    speakWithBrowser,
    speakWithGateway
  ]);

  return {
    enabled,
    setEnabled,
    speak,
    speakAudio,
    speaking,
    stop,
    supported
  };
}

function resolveTtsGatewayUrl(explicitGatewayUrl?: string | null) {
  if (explicitGatewayUrl === null) {
    return "";
  }

  if (typeof explicitGatewayUrl === "string") {
    if (
      explicitGatewayUrl === sameOriginGatewayValue ||
      explicitGatewayUrl === "/tts" ||
      explicitGatewayUrl === "same-origin"
    ) {
      return getSameOriginTtsGatewayUrl();
    }

    if (explicitGatewayUrl.startsWith("/")) {
      return getSameOriginTtsGatewayUrl(explicitGatewayUrl);
    }

    return explicitGatewayUrl;
  }

  if (import.meta.env.VITE_TTS_GATEWAY_URL) {
    if (
      import.meta.env.VITE_TTS_GATEWAY_URL === sameOriginGatewayValue ||
      import.meta.env.VITE_TTS_GATEWAY_URL === "/tts" ||
      import.meta.env.VITE_TTS_GATEWAY_URL === "same-origin"
    ) {
      return getSameOriginTtsGatewayUrl();
    }

    if (import.meta.env.VITE_TTS_GATEWAY_URL.startsWith("/")) {
      return getSameOriginTtsGatewayUrl(import.meta.env.VITE_TTS_GATEWAY_URL);
    }

    return import.meta.env.VITE_TTS_GATEWAY_URL;
  }

  try {
    const configuredVoiceGatewayUrl = import.meta.env.VITE_VOICE_GATEWAY_URL;
    if (
      configuredVoiceGatewayUrl === sameOriginGatewayValue ||
      configuredVoiceGatewayUrl === "/voice" ||
      configuredVoiceGatewayUrl === "same-origin" ||
      configuredVoiceGatewayUrl?.startsWith("/")
    ) {
      return getSameOriginTtsGatewayUrl();
    }

    const voiceGatewayUrl = new URL(configuredVoiceGatewayUrl || defaultVoiceGatewayUrl);
    voiceGatewayUrl.protocol = voiceGatewayUrl.protocol === "wss:" ? "https:" : "http:";
    voiceGatewayUrl.pathname = "/tts";
    voiceGatewayUrl.search = "";
    voiceGatewayUrl.hash = "";
    return voiceGatewayUrl.toString();
  } catch {
    return defaultTtsGatewayUrl;
  }
}

function getSameOriginTtsGatewayUrl(pathname = "/tts") {
  if (typeof window === "undefined") {
    return defaultTtsGatewayUrl;
  }

  return new URL(pathname, window.location.origin).toString();
}

function canUseGatewayTts(preferGateway: boolean, gatewayUrl: string) {
  return (
    preferGateway &&
    Boolean(gatewayUrl) &&
    typeof window !== "undefined" &&
    typeof fetch !== "undefined" &&
    typeof Audio !== "undefined" &&
    typeof URL !== "undefined"
  );
}

function canUseBrowserTts() {
  return typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
}

function pickChineseVoice(voices: SpeechSynthesisVoice[], lang: string) {
  return (
    voices.find((voice) => voice.lang.toLowerCase() === lang.toLowerCase()) ??
    voices.find((voice) => voice.lang.toLowerCase().startsWith("zh")) ??
    null
  );
}
